import React, { useState, useEffect } from "react";
import { FaArrowRight, FaShieldAlt, FaClock, FaDollarSign, FaCar, FaFileAlt, FaHandshake } from "react-icons/fa";
import { collection, getDocs, getFirestore } from "firebase/firestore";
import emailjs from "@emailjs/browser";
import Swal from "sweetalert2";


const EZRentToOwnCompo = () => {

  const firestore = getFirestore();

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    abn: "",
    vehicleType: "",
  });
  const [message, setMessage] = useState("");

  // Vehicle list
  const [vehicleDetailsData, setVehicleDetailsData] = useState([]);

  useEffect(() => {
    const fetchVehicleDetails = async () => {
      const vehiclesCollection = collection(firestore, "vehicles");
      const vehiclesSnapshot = await getDocs(vehiclesCollection);
      const vehiclesData = vehiclesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setVehicleDetailsData(vehiclesData);
    };

    fetchVehicleDetails();
    emailjs.init(process.env.REACT_APP_EMAIL_PUBLIC_KEY);
  }, [firestore]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Validation
  const validatePhoneNumber = (phoneNumber) => {
    const regex = /^(?:\+?61|0)(?:\d{9}|\d{2}\s?\d{4}\s?\d{4})$/;
    return regex.test(phoneNumber);
  };

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const validateForm = () => {
    if (!formData.name) {
      Swal.fire("Name Required", "Please enter your name", "error");
      return false;
    }
    if (!formData.phone || !validatePhoneNumber(formData.phone)) {
      Swal.fire("Phone Invalid", "Enter a valid phone number", "error");
      return false;
    }
    if (!formData.email || !validateEmail(formData.email)) {
      Swal.fire("Email Invalid", "Please enter a valid email address", "error");
      return false;
    }
    if (!formData.abn) {
      Swal.fire("ABN Required", "Please select Yes or No", "error");
      return false;
    }
    if (!formData.vehicleType) {
      Swal.fire("Vehicle Required", "Please select a vehicle type", "error");
      return false;
    }
    return true;
  };

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Enquiry Form submit function called');
    if (!validateForm()) return;

    // Build payload - matching the working BookCar form pattern
    const payload = {
      name: formData.name,
      phone: formData.phone,
      email: formData.email,
      abn: formData.abn,
      vehicleType: formData.vehicleType,
      carType: formData.vehicleType,
      to_email: "info@ezcarrentals.com.au",
      form_source: "Enquiry Form",
      enquiry_type: "Rent to Own Enquiry Form",
    };

    // Only add message if it has content
    if (message && message.trim()) {
      payload.message = message;
    }

    console.log('Enquiry Form payload:', payload);

    try {
      const result = await emailjs.send(
          process.env.REACT_APP_EMAIL_SERVICE_ID,
          process.env.REACT_APP_EMAIL_TEMPLATE_ID,
          payload,
          process.env.REACT_APP_EMAIL_PUBLIC_KEY
      );

      console.log('Enquiry Form email sent successfully:', result);

      Swal.fire({
        icon: "success",
        title: "Request Sent",
        text: "Thank you for enquiring. Our staff will contact you shortly!",
      });

      setFormData({ name: "", phone: "", email: "", abn: "", vehicleType: "" });
      setMessage("");
    } catch (error) {
      console.error('Enquiry Form error sending email:', error);
      Swal.fire({
        icon: "error",
        title: "Failed to Send",
        text: "Something went wrong. Please try again or contact us on 0400 920 512.",
      });
    }
  };

  return (
    <>
      <div className=" ">
        <div className="row ez-rent-to-own">
          <div className="col-md-12">
            {/* Modern Hero Section */}
            <div className="ez-hero-section">
              <div className="ez-hero-background">
                <div className="ez-hero-overlay"></div>
              </div>
              <div className="ez-hero-content">
                <div className="ez-hero-badge">
                  <FaShieldAlt />
                  <span>ABN Holders Only</span>
                </div>
                <h1>
                  EZ Rent to Own
                  <span className="ez-hero-highlight">Drive Now, Own It Later</span>
                </h1>
                <h3>
                  Affordable Business Vehicle Leasing with No Credit Checks
                </h3>
                <p>
                  At EZ Car Rentals, we're excited to launch EZ Rent to Own, a smarter way for business owners,
                  sole traders, and contractors to get behind the wheel of a reliable vehicle — without the hassle
                  of traditional financing.
                </p>
                <div className="ez-hero-stats">
                  <div className="ez-stat-item">
                    <FaDollarSign />
                    <span>From $195/week</span>
                  </div>
                  <div className="ez-stat-item">
                    <FaClock />
                    <span>Quick Approval</span>
                  </div>
                  <div className="ez-stat-item">
                    <FaCar />
                    <span>Own the Vehicle</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Modern Benefits Section */}
            <div className="ez-why-choose">
              <div className="ez-section-header">
                <h2>Why Choose EZ Rent to Own?</h2>
                <p>Everything you need to grow your business, with none of the traditional financing hassles</p>
              </div>

              <div className="ez-benefits-grid">
                <div className="ez-benefit-card">
                  <div className="ez-benefit-icon">
                    <FaShieldAlt />
                  </div>
                  <div className="ez-benefit-content">
                    <h4>No Credit Checks</h4>
                    <p>We focus on your business, not your credit score</p>
                  </div>
                  <div className="ez-benefit-arrow">
                    <FaArrowRight />
                  </div>
                </div>

                <div className="ez-benefit-card">
                  <div className="ez-benefit-icon">
                    <FaDollarSign />
                  </div>
                  <div className="ez-benefit-content">
                    <h4>Affordable Weekly Payments</h4>
                    <p>From just $195/week*</p>
                  </div>
                  <div className="ez-benefit-arrow">
                    <FaArrowRight />
                  </div>
                </div>

                <div className="ez-benefit-card">
                  <div className="ez-benefit-icon">
                    <FaHandshake />
                  </div>
                  <div className="ez-benefit-content">
                    <h4>Low Upfront Payments</h4>
                    <p>From just $780 onwards</p>
                  </div>
                  <div className="ez-benefit-arrow">
                    <FaArrowRight />
                  </div>
                </div>

                <div className="ez-benefit-card">
                  <div className="ez-benefit-icon">
                    <FaFileAlt />
                  </div>
                  <div className="ez-benefit-content">
                    <h4>Tax-Deductible</h4>
                    <p>Claim lease payments as a business expense*</p>
                  </div>
                  <div className="ez-benefit-arrow">
                    <FaArrowRight />
                  </div>
                </div>

                <div className="ez-benefit-card">
                  <div className="ez-benefit-icon">
                    <FaCar />
                  </div>
                  <div className="ez-benefit-content">
                    <h4>Own the Vehicle at the End</h4>
                    <p>No balloon payment surprises</p>
                  </div>
                  <div className="ez-benefit-arrow">
                    <FaArrowRight />
                  </div>
                </div>

                <div className="ez-benefit-card">
                  <div className="ez-benefit-icon">
                    <FaClock />
                  </div>
                  <div className="ez-benefit-content">
                    <h4>Easy Approval</h4>
                    <p>Fast, simple process with minimal paperwork</p>
                  </div>
                  <div className="ez-benefit-arrow">
                    <FaArrowRight />
                  </div>
                </div>
              </div>
            </div>

            {/* Modern Who It's For Section */}
            <div className="ez-who-section">
              <div className="ez-who-container">
                <div className="ez-who-content">
                  <div className="ez-section-header">
                    <h2>WHO IS IT FOR</h2>
                    <p>Perfect for Melbourne's hardworking business owners</p>
                  </div>
                  <div className="ez-who-text">
                    <p>
                      If you're a tradie, delivery driver, Uber/DoorDash partner, or small business owner in Melbourne
                      needing a vehicle to grow your income — this is the perfect solution. Our well-maintained fleet
                      includes manual and automatic vans, utes, and sedans.
                    </p>
                  </div>
                  <div className="ez-who-tags">
                    <span className="ez-tag">Tradies</span>
                    <span className="ez-tag">Delivery Drivers</span>
                    <span className="ez-tag">Uber/DoorDash</span>
                    <span className="ez-tag">Small Business</span>
                    <span className="ez-tag">Contractors</span>
                    <span className="ez-tag">Sole Traders</span>
                  </div>
                </div>
                <div className="ez-who-visual">


     <div className="rentto-card">
      <h2 className="rentto-title">Enquiry Form</h2>

      <form onSubmit={handleSubmit} className="rentto-form">
        {/* Name */}
        <div className="rentto-group">
          <label className="rentto-label">Name</label>
          <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="rentto-input"
              placeholder="Enter your name"
          />
        </div>

        {/* Phone */}
        <div className="rentto-group">
          <label className="rentto-label">Phone</label>
          <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              className="rentto-input"
              placeholder="Enter your phone number"
          />
        </div>

        {/* Email */}
        <div className="rentto-group">
          <label className="rentto-label">Email</label>
          <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="rentto-input"
              placeholder="Enter your email address"
          />
        </div>

        {/* ABN */}
        <div className="rentto-group">
          <span className="rentto-label">Do you work on ABN?</span>
          <div className="rentto-radio-group">
            <label className="rentto-radio">
              <input
                  type="radio"
                  name="abn"
                  value="Yes"
                  checked={formData.abn === "Yes"}
                  onChange={handleChange}
              />
              <span>Yes</span>
            </label>
            <label className="rentto-radio">
              <input
                  type="radio"
                  name="abn"
                  value="No"
                  checked={formData.abn === "No"}
                  onChange={handleChange}
              />
              <span>No</span>
            </label>
          </div>
        </div>

        {/* Vehicle Type */}
        <div className="rentto-group">
          <label className="rentto-label">Type of Vehicle</label>
          <select
              name="vehicleType"
              value={formData.vehicleType}
              onChange={handleChange}
              required
              className="rentto-input"
          >
            <option value="">Select vehicle type</option>
            {vehicleDetailsData.map((v) => (
                <option key={v.id} value={v.vehicleName}>{v.vehicleName}</option>
            ))}
          </select>
        </div>

        {/* Submit */}
        <button type="submit" className="rentto-button">
          Submit Enquiry
        </button>
      </form>
    </div>


                </div>
              </div>
            </div>

            {/* Modern How It Works Section */}
            <div className="ez-how-works">
              <div className="ez-section-header">
                <h2>How It Works</h2>
                <p>Simple 6-step process to get you on the road</p>
              </div>

              <div className="ez-steps-container">
                <div className="ez-steps-grid">
                  <div className="ez-step-card">
                    <div className="ez-step-number">1</div>
                    <div className="ez-step-content">
                      <h4>Choose Your Vehicle</h4>
                      <p>Select from our well-maintained fleet</p>
                    </div>
                  </div>

                  <div className="ez-step-card">
                    <div className="ez-step-number">2</div>
                    <div className="ez-step-content">
                      <h4>Provide Documents</h4>
                      <p>ABN, ID + Secondary proof of address</p>
                    </div>
                  </div>

                  <div className="ez-step-card">
                    <div className="ez-step-number">3</div>
                    <div className="ez-step-content">
                      <h4>Income Proof</h4>
                      <p>Last month's invoices + bank statement</p>
                    </div>
                  </div>

                  <div className="ez-step-card">
                    <div className="ez-step-number">4</div>
                    <div className="ez-step-content">
                      <h4>Drive Away</h4>
                      <p>Get approved and start using your vehicle</p>
                    </div>
                  </div>

                  <div className="ez-step-card">
                    <div className="ez-step-number">5</div>
                    <div className="ez-step-content">
                      <h4>Make Payments</h4>
                      <p>Affordable weekly payments</p>
                    </div>
                  </div>

                  <div className="ez-step-card">
                    <div className="ez-step-number">6</div>
                    <div className="ez-step-content">
                      <h4>Own the Vehicle</h4>
                      <p>Complete payments and it's yours</p>
                    </div>
                  </div>
                </div>
                {/*<div className="ez-steps-connector"></div>*/}
              </div>
            </div>

            {/* Modern Contact Section */}
            {/*<div className="ez-contact-section">*/}
            {/*  <div className="ez-contact-background">*/}
            {/*    <div className="ez-contact-overlay"></div>*/}
            {/*  </div>*/}
            {/*  <div className="ez-contact-content">*/}
            {/*    <div className="ez-section-header">*/}
            {/*      <h2>Ready to Get Started?</h2>*/}
            {/*      <p>Join hundreds of Melbourne business owners who trust EZ Car Rentals</p>*/}
            {/*    </div>*/}

            {/*    <div className="ez-contact-container">*/}
            {/*      <div className="ez-contact-form">*/}
            {/*        <div className="ez-form-header">*/}
            {/*          <h3>Enquire Now</h3>*/}
            {/*          <p>We'll get back to you within 24 hours</p>*/}
            {/*        </div>*/}
            {/*        <form onSubmit={handleSubmit}>*/}
            {/*          <div className="ez-form-group">*/}
            {/*            <label htmlFor="name">Full Name</label>*/}
            {/*            <input*/}
            {/*              type="text"*/}
            {/*              id="name"*/}
            {/*              name="name"*/}
            {/*              value={formData.name}*/}
            {/*              onChange={handleInputChange}*/}
            {/*              placeholder="Enter your full name"*/}
            {/*              required*/}
            {/*            />*/}
            {/*          </div>*/}

            {/*          <div className="ez-form-group">*/}
            {/*            <label htmlFor="phone">Phone Number</label>*/}
            {/*            <input*/}
            {/*              type="tel"*/}
            {/*              id="phone"*/}
            {/*              name="phone"*/}
            {/*              value={formData.phone}*/}
            {/*              onChange={handleInputChange}*/}
            {/*              placeholder="Enter your phone number"*/}
            {/*              required*/}
            {/*            />*/}
            {/*          </div>*/}

            {/*          <div className="ez-form-group">*/}
            {/*            <label htmlFor="modelMake">Vehicle Interest</label>*/}
            {/*            <input*/}
            {/*              type="text"*/}
            {/*              id="modelMake"*/}
            {/*              name="modelMake"*/}
            {/*              value={formData.modelMake}*/}
            {/*              onChange={handleInputChange}*/}
            {/*              placeholder="e.g., Toyota Hilux, Ford Transit"*/}
            {/*            />*/}
            {/*          </div>*/}

            {/*          <button type="submit" className="ez-submit-btn">*/}
            {/*            <span>Submit Enquiry</span>*/}
            {/*            <FaArrowRight />*/}
            {/*          </button>*/}
            {/*        </form>*/}
            {/*      </div>*/}

            {/*      <div className="ez-contact-info">*/}
            {/*        <div className="ez-contact-header">*/}
            {/*          <h3>Get in Touch</h3>*/}
            {/*          <p>Multiple ways to reach us</p>*/}
            {/*        </div>*/}
            {/*        <div className="ez-contact-methods">*/}
            {/*          <a href="tel:0400920512" className="ez-contact-method">*/}
            {/*            <div className="ez-contact-icon">*/}
            {/*              <FaPhone />*/}
            {/*            </div>*/}
            {/*            <div className="ez-contact-details">*/}
            {/*              <span className="ez-contact-label">Call Us</span>*/}
            {/*              <span className="ez-contact-value">0400 920 512</span>*/}
            {/*            </div>*/}
            {/*          </a>*/}
            {/*          */}
            {/*          <a href="mailto:imkaluwa@gmail.com" className="ez-contact-method">*/}
            {/*            <div className="ez-contact-icon">*/}
            {/*              <FaEnvelope />*/}
            {/*            </div>*/}
            {/*            <div className="ez-contact-details">*/}
            {/*              <span className="ez-contact-label">Email Us</span>*/}
            {/*              <span className="ez-contact-value">imkaluwa@gmail.com</span>*/}
            {/*            </div>*/}
            {/*          </a>*/}
            {/*          */}
            {/*          <button onClick={handleWhatsAppCall} className="ez-contact-method">*/}
            {/*            <div className="ez-contact-icon">*/}
            {/*              <FaWhatsapp />*/}
            {/*            </div>*/}
            {/*            <div className="ez-contact-details">*/}
            {/*              <span className="ez-contact-label">WhatsApp</span>*/}
            {/*              <span className="ez-contact-value">Chat with us</span>*/}
            {/*            </div>*/}
            {/*          </button>*/}
            {/*        </div>*/}
            {/*      </div>*/}
            {/*    </div>*/}
            {/*  </div>*/}
            {/*</div>*/}

            {/* Terms Section */}
            {/*<div className="ez-terms-section">*/}
            {/*  <p className="ez-terms-text">*conditions apply.</p>*/}
            {/*</div>*/}

          </div>
        </div>
      </div>
    </>
  );
};

export default EZRentToOwnCompo;
